<!--440-->
<template>
  <div class="pb-16 pt-12 body-font neutral-background-1">
    <div class="w-full grid grid-cols-7 md:grid-cols-4 xs:grid-cols-3 xs:px-4">
      <div
        class="profile-wrapper neutral-background-2 col-span-5 col-start-2 xs:col-span-3 xs:col-start-1 md:col-span-2 md:col-start-2"
      >
        <div class="flex justify-center items-center flex-col relative">
          <div class="flex justify-start items-start w-full">
            <div>
              <Avatar />
            </div>

            <div class="w-auto flex-1 min-w-0">
              <!-- Name -->
              <title-text class="text-2xl flex justify-start items-baseline">
                <edit-icon v-if="!nameEdit" @click="nameEdit = true" />
                <span v-if="!nameEdit">{{ user.name }}</span>
                <hidden-input
                  v-else
                  :active="nameEdit"
                  :errors="nameErrors"
                  class="text-sm"
                  textarea
                  @cancel="
                    name = user.name;
                    nameEdit = false;
                  "
                  @save="saveName"
                >
                  <textarea v-model="name" type="text" @input="$v.name.$touch()"></textarea>
                </hidden-input>
              </title-text>
              <!-- Expertise -->
              <p class="my-3 flex justify-start items-baseline mb-3">
                <edit-icon v-if="!expertiseEdit" @click="expertiseEdit = true" />
                <title-text class="text-md mr-2">{{ $t("coach.info.1") }}</title-text>
              </p>

              <p v-if="!expertiseEdit">{{ expertise ? expertise : $t("coach.info.1.subtitle") }}</p>
              <hidden-input
                v-else
                :active="expertiseEdit"
                :errors="expertiseErrors"
                textarea
                @cancel="
                  expertise = user.expertise;
                  expertiseEdit = false;
                "
                @save="saveExpertise"
              >
                <textarea v-model="expertise" type="text" @input="$v.expertise.$touch()"></textarea>
              </hidden-input>

              <!-- Qualification -->
              <p class="my-3 flex justify-start items-baseline mb-3">
                <edit-icon v-if="!qualificationEdit" @click="qualificationEdit = true" />
                <title-text class="text-md mr-2 mt-3">{{ $t("coach.info.2") }}</title-text>
              </p>
              <p v-if="!qualificationEdit" :class="{ 'inactive-text': !qualification }">
                {{ qualification ? qualification : $t("coach.info.2.subtitle") }}
              </p>
              <hidden-input
                v-else
                :active="qualificationEdit"
                :errors="qualificationErrors"
                textarea
                @cancel="
                  qualification = user.qualification;
                  qualificationEdit = false;
                "
                @save="saveQualification"
              >
                <textarea v-model="qualification" type="text" @input="$v.qualification.$touch()"></textarea>
              </hidden-input>

              <!-- About -->
              <div class="flex flex-col justify-start items-baseline my-8">
                <span class="flex justify-start items-baseline mb-4">
                  <edit-icon v-if="!aboutEdit" @click="aboutEdit = true" />
                  <title-text class="text-md mr-2">{{ $t("coach.info.3") }}</title-text>
                </span>
                <span v-if="(about.length === 0 || about === '‎') && !aboutEdit" class="inactive-text">
                  {{ $t("coach.info.3.subtitle") }}
                </span>
                <span v-if="!aboutEdit">{{ about }}</span>
                <hidden-input
                  v-else
                  :active="aboutEdit"
                  :errors="aboutErrors"
                  textarea
                  @cancel="
                    about = user.about;
                    aboutEdit = false;
                  "
                  @save="saveAbout"
                >
                  <textarea v-model="about" :placeholder="$t('tell.about')" type="text"></textarea>
                </hidden-input>
              </div>
              <!-- Languages -->
              <span class="flex justify-start items-baseline mb-4">
                <edit-icon v-if="!languagesEdit" @click="languagesEdit = true" />
                <title-text class="text-md mr-2">{{ $t("coach.info.4") }}</title-text>
              </span>
              <div v-if="user.languages" class="flex justify-start items-center flex-wrap">
                <div v-if="!languagesEdit" class="flex justify-start items-center flex-wrap">
                  <div v-for="l in selectedLanguages" :key="l.id + 'selected'" class="language-2">
                    <img v-if="l.media" :alt="l.name" :src="l.media" class="w-6 mr-2" />
                    {{ l.name }}
                  </div>
                </div>
                <div v-else>
                  <div class="flex justify-start items-center flex-wrap w-full">
                    <div
                      v-for="l in languages"
                      :key="l.id"
                      :class="selectedLanguagesID.indexOf(l.id) >= 0 ? 'primary-background text-black' : 'primary-text'"
                      class="language"
                      @click="choseLanguage(l.id)"
                    >
                      <img v-if="l.media" :alt="l.name" :src="l.media" class="w-6 mr-2" />
                      {{ l.name }}
                    </div>
                  </div>
                  <p v-if="languagesErrors.length > 0" class="text-error text-13 ml-6 my-1 w-full text-left">
                    {{ languagesErrors[0] }}
                  </p>
                </div>
                <div v-if="languagesEdit" class="w-full mt-4">
                  <primary-button sm @click="saveLanguages">{{ $t("save.generic") }}</primary-button>
                  <secondary-button class="ml-2" sm @click="cancelLanguages">
                    {{ $t("cancel.generic") }}
                  </secondary-button>
                </div>
              </div>

              <p v-else class="mt-4">{{ $t("coach.info.4.subtitle") }}</p>

              <!-- Coaching areas -->
              <span class="flex justify-start items-baseline my-4">
                <edit-icon v-if="!coachingEdit" @click="coachingEdit = true" />
                <title-text class="text-md mr-2">{{ $t("coach.info.5") }}</title-text>
              </span>
              <div class="flex justify-start items-center flex-wrap">
                <div v-if="!coachingEdit" class="flex justify-start items-center flex-wrap">
                  <div v-if="user.area.length === 0" class="inactive-text">{{ $t("coach.info.5.subtitle") }}</div>
                  <div v-for="a in selectedCoaching" :key="a.id + 'selected_area'" class="coaching">
                    {{ a.name }}
                  </div>
                </div>
                <div v-else>
                  <div class="flex justify-start items-center flex-wrap w-full">
                    <div
                      v-for="l in coachingAreas"
                      :key="l.id + '_area'"
                      :class="selectedCoachingID.indexOf(l.id) >= 0 ? 'primary-background text-black' : 'primary-text'"
                      class="areas"
                      @click="choseCoachingArea(l.id)"
                    >
                      {{ l.name }}
                    </div>
                  </div>
                  <p v-if="coachingErrors.length > 0" class="text-error text-13 ml-6 my-1 w-full text-left">
                    {{ coachingErrors[0] }}
                  </p>
                </div>
                <div v-if="coachingEdit" class="w-full mt-4">
                  <primary-button sm @click="saveCoaching">{{ $t("save.generic") }}</primary-button>
                  <secondary-button class="ml-2" sm @click="cancelCoaching">
                    {{ $t("cancel.generic") }}
                  </secondary-button>
                </div>
              </div>
              <!-- Partner Affiliation -->
              <div v-if="hasPartnerAffiliation && showPartnerAffiliations">
                <div class="my-8 flex flex-col justify-start items-baseline">
                  <span class="flex justify-start items-baseline mb-4">
                    <edit-icon v-if="!partnerAffiliationEdit" @click="partnerAffiliationEdit = true" />
                    <title-text class="text-md mr-2">{{ $t("partner.affiliation") }}</title-text>
                  </span>

                  <!-- View -->
                  <span v-if="!partnerAffiliationEdit" :class="{ 'inactive-text': !partner_affiliation }">
                    {{ partnerAffiliationLabel }}
                  </span>

                  <!-- Edit -->
                  <div v-else>
                    <div class="flex justify-start items-center flex-wrap">
                      <div
                        v-for="item in partnerAffiliationOptions"
                        :key="item.value"
                        class="px-5 py-2 border-2 mr-2 mb-2 cursor-pointer"
                        :class="
                          partner_affiliation === item.value
                            ? 'primary-background text-white'
                            : 'primary-text border-primary'
                        "
                        @click="partner_affiliation = item.value"
                      >
                        {{ item.label }}
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Session price -->
                <div
                  v-if="hasSessionPrice && (showPaymentLink || showBookingLink)"
                  class="my-8 flex flex-col justify-start items-baseline"
                >
                  <span class="flex justify-start items-baseline mb-4">
                    <edit-icon v-if="!sessionPriceEdit" @click="sessionPriceEdit = true" />
                    <title-text class="text-md mr-2">{{ $t("sessions.session_price") }}</title-text>
                  </span>

                  <span v-if="!sessionPriceEdit" :class="{ 'inactive-text': session_price == null }">
                    {{ session_price != null ? "€ " + session_price : $t("sessions.no_price") }}
                  </span>

                  <hidden-input
                    v-else
                    :active="sessionPriceEdit"
                    :errors="sessionPriceErrors"
                    @cancel="
                      session_price = user.session_price;
                      sessionPriceEdit = false;
                    "
                  >
                    <input
                      v-model="session_price"
                      type="text"
                      class="w-full"
                      @input="onPriceInput"
                      inputmode="decimal"
                    />
                  </hidden-input>
                </div>
                <!-- Payment link -->
                <div
                  v-if="hasPaymentLink && showPaymentLink"
                  class="my-8 w-full flex flex-col justify-start items-start"
                >
                  <span class="flex justify-start items-baseline mb-2">
                    <edit-icon v-if="!paymentLinkEdit" @click="paymentLinkEdit = true" />
                    <title-text class="text-md mr-2">{{ $t("payment.link") }}</title-text>
                  </span>
                  <!-- View -->
                  <span
                    v-if="!paymentLinkEdit"
                    :class="{ 'inactive-text': !payment_link }"
                    class="text-blue-700 underline cursor-pointer w-full max-w-full inline-block"
                  >
                    <a class="link-ellipsis" v-if="payment_link" :href="payment_link" target="_blank">
                      {{ payment_link }}
                    </a>
                    <span v-else>{{ $t("payment.link.no") }}</span>
                  </span>
                  <!-- Edit -->
                  <hidden-input
                    v-else
                    :active="paymentLinkEdit"
                    :errors="paymentLinkErrors"
                    @cancel="
                      payment_link = user.payment_link;
                      paymentLinkEdit = false;
                    "
                  >
                    <input
                      v-model="payment_link"
                      type="text"
                      placeholder="https://example.com"
                      class="w-full"
                      @input="validatePaymentLink"
                    />
                  </hidden-input>
                </div>
                <!-- Booking link -->
                <div
                  v-if="hasBookingLink && showBookingLink"
                  class="my-8 w-full flex flex-col justify-start items-start"
                >
                  <span class="flex justify-start items-baseline mb-2">
                    <edit-icon v-if="!bookingLinkEdit" @click="bookingLinkEdit = true" />
                    <title-text class="text-md mr-2">{{ $t("booking.link") }}</title-text>
                  </span>
                  <!-- View -->
                  <span
                    v-if="!bookingLinkEdit"
                    :class="{ 'inactive-text': !booking_link }"
                    class="text-blue-700 underline cursor-pointer w-full max-w-full inline-block"
                  >
                    <a class="link-ellipsis" v-if="booking_link" :href="booking_link" target="_blank">
                      {{ booking_link }}
                    </a>
                    <span v-else>{{ $t("booking.link.no") }}</span>
                  </span>
                  <!-- Edit -->
                  <hidden-input
                    v-else
                    :active="bookingLinkEdit"
                    :errors="bookingLinkErrors"
                    @cancel="
                      booking_link = user.booking_link;
                      bookingLinkEdit = false;
                    "
                  >
                    <input
                      v-model="booking_link"
                      type="text"
                      placeholder="https://example.com"
                      class="w-full"
                      @input="validateBookingLink"
                    />
                  </hidden-input>
                </div>
                <!-- save/cancel for partner section -->
                <div
                  v-if="partnerAffiliationEdit || sessionPriceEdit || paymentLinkEdit || bookingLinkEdit"
                  class="w-full mt-4"
                >
                  <primary-button sm @click="savePartnerSection">{{ $t("save.generic") }}</primary-button>
                  <secondary-button class="ml-2" sm @click="cancelPartnerSection">
                    {{ $t("cancel.generic") }}
                  </secondary-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PrimaryButton from "@/components/ui-kit/buttons/PrimaryButton";
import { mapState } from "vuex";
import TitleText from "@/components/ui-kit/typography/TitleText";
import EditIcon from "@/components/ui-kit/icons/EditIcon";
import HiddenInput from "@/components/ui-kit/form/HiddenInput";
import { maxLength, minLength, required } from "vuelidate/lib/validators";
import SecondaryButton from "@/components/ui-kit/buttons/SecondaryButton";
import Avatar from "@/components/profile/Avatar";
import { PARTNER_AFFILIATIONS } from "@/constants";

const nameValidator = (value) => {
  return value.indexOf(" ") !== -1 && value.length > value.indexOf(" ") + 2;
};

export default {
  name: "Home",
  components: {
    Avatar,
    SecondaryButton,
    HiddenInput,
    EditIcon,
    TitleText,
    PrimaryButton
  },
  data() {
    return {
      expertise: "",
      expertiseEdit: false,
      qualification: "",
      qualificationEdit: false,
      about: "",
      aboutEdit: false,
      name: "",
      nameEdit: false,
      languagesEdit: false,
      selectedLanguages: [],
      selectedLanguagesID: [],
      languagesErrors: [],
      coachingEdit: false,
      selectedCoaching: [],
      selectedCoachingID: [],
      coachingErrors: [],
      session_price: null,
      sessionPriceDirty: false,
      sessionPriceEdit: false,
      partner_affiliation: null,
      partnerAffiliationEdit: false,
      payment_link: "",
      paymentLinkEdit: false,
      paymentLinkErrors: [],
      booking_link: "",
      bookingLinkEdit: false,
      bookingLinkErrors: [],
      showPartnerAffiliations: process.env.VUE_APP_SHOW_PARTNER_AFFILIATIONS === "true"
    };
  },
  computed: {
    ...mapState("auth", ["user"]),
    ...mapState("common", ["languages", "coachingAreas"]),

    expertiseErrors() {
      const errors = [];
      if (!this.$v.expertise.$dirty) return errors;
      !this.$v.expertise.required && errors.push(this.$t("expertise.required"));
      !this.$v.expertise.minLength && errors.push(this.$t("expertise.longer", { count: "2" }));
      !this.$v.expertise.maxLength && errors.push(this.$t("expertise.shorter", { count: "200" }));
      return errors;
    },

    nameErrors() {
      const errors = [];
      if (!this.$v.name.$dirty) return errors;
      !this.$v.name.required && errors.push(this.$t("name.required"));
      !this.$v.name.minLength && errors.push(this.$t("name.longer", { count: "2" }));
      !this.$v.name.nameValidator && errors.push(this.$t("name.two.strings"));
      return errors;
    },

    qualificationErrors() {
      const errors = [];
      if (!this.$v.qualification.$dirty) return errors;
      !this.$v.qualification.required && errors.push(this.$t("qualifications.required"));
      !this.$v.qualification.minLength && errors.push(this.$t("qualifications.longer", { count: "2" }));
      !this.$v.qualification.maxLength && errors.push(this.$t("qualifications.shorter", { count: "200" }));
      return errors;
    },
    aboutErrors() {
      const errors = [];
      if (!this.$v.about?.$dirty) return errors;
      if (!this.$v.about.required) errors.push(this.$t("about.required"));
      if (!this.$v.about.minLength) errors.push(this.$t("about.longer", { count: "2" }));
      return errors;
    },
    sessionPriceErrors() {
      const errors = [];
      if (!this.sessionPriceDirty) return errors;
      if (this.session_price === null || this.session_price === "") {
        errors.push(this.$t("sessions.price.required"));
      } else if (isNaN(this.session_price)) {
        errors.push(this.$t("number.invalid"));
      }
      return errors;
    },
    /** ======= Backend field existence checks ======= */
    hasPartnerAffiliation() {
      return Object.prototype.hasOwnProperty.call(this.user, "partner_affiliation");
    },

    hasPaymentLink() {
      return Object.prototype.hasOwnProperty.call(this.user, "payment_link");
    },

    hasBookingLink() {
      return Object.prototype.hasOwnProperty.call(this.user, "booking_link");
    },

    hasSessionPrice() {
      return Object.prototype.hasOwnProperty.call(this.user, "session_price");
    },

    /** ========== Partner logic ========== */

    partnerAffiliationOptions() {
      return [
        { value: PARTNER_AFFILIATIONS.NOT_AFFILIATED, label: this.$t("partner.not_affiliated") },
        { value: PARTNER_AFFILIATIONS.SOLIDARIS, label: "Solidaris" },
        { value: PARTNER_AFFILIATIONS.COHEZIO, label: "Cohezio" }
      ];
    },

    partnerAffiliationLabel() {
      const f = this.partnerAffiliationOptions.find((o) => o.value === this.partner_affiliation);
      return f ? f.label : this.$t("partner.not_affiliated");
    },

    showPaymentLink() {
      return (
        this.partner_affiliation === PARTNER_AFFILIATIONS.COHEZIO ||
        this.partner_affiliation === PARTNER_AFFILIATIONS.NOT_AFFILIATED
      );
    },

    showBookingLink() {
      return this.partner_affiliation === PARTNER_AFFILIATIONS.SOLIDARIS;
    }
  },

  watch: {
    selectedLanguages() {
      this.selectedLanguagesID = this.selectedLanguages.map((i) => i.id);
    },

    selectedCoaching() {
      this.selectedCoachingID = this.selectedCoaching.map((i) => i.id);
    },

    sessionPriceEdit(value) {
      if (!value) {
        this.sessionPriceDirty = false;
      }
    }
  },
  methods: {
    /** ========== Build payload ========== */
    buildPayload(fields = []) {
      const payload = {};
      if (fields.includes("name")) payload.name = this.name;
      if (fields.includes("expertise")) payload.expertise = this.expertise;
      if (fields.includes("qualification")) payload.qualification = this.qualification;
      if (fields.includes("about")) payload.about = this.about;
      if (fields.includes("area")) payload.area = this.selectedCoachingID;
      if (fields.includes("language")) payload.language = this.selectedLanguagesID;
      if (fields.includes("partner_affiliation") && this.hasPartnerAffiliation) {
        payload.partner_affiliation = this.partner_affiliation;
      }
      if (fields.includes("session_price") && this.hasSessionPrice) {
        payload.session_price = this.showPaymentLink || this.showBookingLink ? this.session_price ?? null : null;
      }
      if (fields.includes("payment_link") && this.hasPaymentLink) {
        payload.payment_link = this.showPaymentLink ? this.payment_link || null : null;
      }
      if (fields.includes("booking_link") && this.hasBookingLink) {
        payload.booking_link = this.showBookingLink ? this.booking_link || null : null;
      }
      return payload;
    },
    /** ========== Sync UI from backend ========== */
    syncLocalWithUser() {
      this.name = this.user.name;
      this.expertise = this.user.expertise;
      this.qualification = this.user.qualification;
      this.about = this.user.about;

      this.selectedLanguages = this.user.languages;
      this.selectedLanguagesID = this.user.languages.map((i) => i.id);

      this.selectedCoaching = this.user.area;
      this.selectedCoachingID = this.user.area.map((i) => i.id);

      if (this.hasPartnerAffiliation) {
        this.partner_affiliation = this.user.partner_affiliation || PARTNER_AFFILIATIONS.NOT_AFFILIATED;
      }
      if (this.hasSessionPrice) {
        this.session_price = this.user.session_price ?? null;
      }
      if (this.hasPaymentLink) {
        this.payment_link = this.user.payment_link || "";
      }
      if (this.hasBookingLink) {
        this.booking_link = this.user.booking_link || "";
      }
    },

    /** ========== Partner (3 fields) ========== */
    async savePartnerSection() {
      this.sessionPriceDirty = true;
      this.validatePaymentLink();
      this.validateBookingLink();
      if (
        this.sessionPriceErrors.length > 0 ||
        this.paymentLinkErrors.length > 0 ||
        this.bookingLinkErrors.length > 0
      ) {
        return;
      }
      const linkField = this.showBookingLink ? "booking_link" : "payment_link";
      await this.$store.dispatch(
        "auth/updateProfilePartial",
        this.buildPayload(["partner_affiliation", "session_price", linkField])
      );
      this.partnerAffiliationEdit = false;
      this.sessionPriceEdit = false;
      this.paymentLinkEdit = false;
      this.bookingLinkEdit = false;
      await this.$store.dispatch("auth/getMe");
      this.syncLocalWithUser();
    },

    cancelPartnerSection() {
      this.syncLocalWithUser();
      this.sessionPriceDirty = false;
      this.partnerAffiliationEdit = false;
      this.sessionPriceEdit = false;
      this.paymentLinkEdit = false;
      this.bookingLinkEdit = false;
    },

    /** ========== Validation ========== */
    validatePaymentLink() {
      this.paymentLinkErrors = [];
      if (this.showPaymentLink && !this.payment_link) {
        this.paymentLinkErrors.push(this.$t("payment.link.required"));
      } else if (this.payment_link && !this.isValidUrl(this.payment_link)) {
        this.paymentLinkErrors.push(this.$t("url.invalid"));
      }
    },
    validateBookingLink() {
      this.bookingLinkErrors = [];
      if (this.showBookingLink && !this.booking_link) {
        this.bookingLinkErrors.push(this.$t("booking.link.required"));
      } else if (this.booking_link && !this.isValidUrl(this.booking_link)) {
        this.bookingLinkErrors.push(this.$t("url.invalid"));
      }
    },
    onPriceInput(e) {
      let v = e.target.value.replace(/[^0-9]/g, "");
      this.session_price = v === "" ? null : Number(v);
    },
    isValidUrl(value) {
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },
    /** ========== SAVE ========== */
    async saveExpertise() {
      this.$v.$touch();
      if (this.$v.expertise.$invalid) return;

      await this.$store.dispatch("auth/updateProfilePartial", this.buildPayload(["expertise"]));
      this.expertiseEdit = false;
      await this.$store.dispatch("auth/getMe");
      this.syncLocalWithUser();
    },

    async saveQualification() {
      this.$v.$touch();
      if (this.$v.qualification.$invalid) return;
      await this.$store.dispatch("auth/updateProfilePartial", this.buildPayload(["qualification"]));
      this.qualificationEdit = false;
      await this.$store.dispatch("auth/getMe");
      this.syncLocalWithUser();
    },

    async saveAbout() {
      this.$v.about.$touch();
      if (this.$v.about.$invalid) return;
      await this.$store.dispatch("auth/updateProfilePartial", this.buildPayload(["about"]));
      this.aboutEdit = false;

      await this.$store.dispatch("auth/getMe");
      this.syncLocalWithUser();
    },

    choseLanguage(id) {
      if (this.selectedLanguagesID.includes(id)) {
        this.selectedLanguagesID = this.selectedLanguagesID.filter((i) => i !== id);
      } else {
        this.selectedLanguagesID.push(id);
      }
      this.languagesErrors = [];
    },

    choseCoachingArea(id) {
      if (this.selectedCoachingID.includes(id)) {
        this.selectedCoachingID = this.selectedCoachingID.filter((i) => i !== id);
      } else {
        this.selectedCoachingID.push(id);
      }
      this.coachingErrors = [];
    },

    async saveLanguages() {
      if (this.selectedLanguagesID.length === 0) {
        this.languagesErrors.push(this.$t("choose.language", { count: "1" }));
        return;
      }

      await this.$store.dispatch("auth/updateProfilePartial", this.buildPayload(["language"]));
      this.languagesEdit = false;
      await this.$store.dispatch("auth/getMe");
      this.syncLocalWithUser();
    },

    async saveCoaching() {
      if (this.selectedCoachingID.length === 0) {
        this.coachingErrors.push(this.$t("choose.area", { count: "1" }));
        return;
      }

      await this.$store.dispatch("auth/updateProfilePartial", this.buildPayload(["area"]));
      this.coachingEdit = false;
      await this.$store.dispatch("auth/getMe");
      this.syncLocalWithUser();
    },

    async saveName() {
      this.$v.$touch();
      if (this.$v.name.$invalid) return;
      await this.$store.dispatch("auth/updateProfilePartial", this.buildPayload(["name"]));
      this.nameEdit = false;

      await this.$store.dispatch("auth/getMe");
      this.syncLocalWithUser();
    },

    cancelCoaching() {
      this.syncLocalWithUser();
      this.coachingEdit = false;
    },

    cancelLanguages() {
      this.syncLocalWithUser();
      this.languagesEdit = false;
    }
  },

  validations: {
    expertise: { required, minLength: minLength(2), maxLength: maxLength(200) },
    qualification: { required, minLength: minLength(2), maxLength: maxLength(200) },
    name: { required, minLength: minLength(3), nameValidator },
    about: { required, minLength: minLength(2) }
  },

  async created() {
    await this.$store.dispatch("common/getLanguages");
    await this.$store.dispatch("common/getCoachingAreas");
    this.syncLocalWithUser();
  }
};
</script>

<style scoped>
.profile-wrapper {
  @apply px-6 py-8 rounded-xl w-full shadow-sm;
  background-color: var(--neutral-background-2);
}

.language {
  @apply px-5 py-2 border-2 transition-all duration-300 cursor-pointer  mr-2 mb-2 flex items-center justify-center;
  border-radius: var(--tag-border-radius);
  border-color: var(--primary-background);
}

.language-2 {
  @apply px-5 py-2  border-2 flex items-center justify-center mr-2 mb-2;
  border-radius: var(--tag-border-radius);
  border-color: var(--neutral-background-1);
  background-color: var(--neutral-background-4);
}

.coaching {
  @apply px-5 py-2 border-2 flex items-center justify-center mr-2 mb-2;
  border-radius: var(--tag-border-radius);
  border-color: var(--neutral-background-1);
  background-color: var(--neutral-background-4);
}

.areas {
  @apply px-5 py-2 border-2 transition-all duration-300 cursor-pointer mr-2 mb-2 flex items-center justify-center;
  border-radius: var(--tag-border-radius);
  border-color: var(--primary-background);
}

.link-ellipsis {
  display: block;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
